import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Params, QueryParamsHandling, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

import { CypressTagDirective } from '@vlaio/cypress/core';

import { VlaioLinkAbstractComponent } from '../shared';

/**
 * A component that renders an internal link. The navigation is done using the Angular Router.
 */
@Component({
	selector: 'vlaio-internal-link',
	template: ` <a
		class="u-outline u-link__underline"
		routerLinkActive="is-active"
		[id]="linkId"
		[ngClass]="{ 'font-bold primary-color': linkStyle === 'styled' }"
		[title]="title ? (i18nKeys.Links.Internal | translate: { title }) : ''"
		[routerLink]="link"
		[queryParams]="linkQueryParams"
		[queryParamsHandling]="linkQueryParamsHandling"
		[cypressTag]="linkCypressTag"
		(isActiveChange)="handleLickActive($event)"
	>
		<ng-content></ng-content>
	</a>`,
	styleUrls: ['../shared/shared-link.style.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [RouterLink, TranslateModule, NgClass, RouterLinkActive, CypressTagDirective]
})
export class VlaioInternalLinkComponent extends VlaioLinkAbstractComponent {
	/**
	 * The fragments (parameters) to be added to the URL query.
	 */
	@Input() public linkQueryParams: Params;

	/**
	 * How to handle query parameters in the URL.
	 *
	 * Default value is `merge`.
	 */
	@Input() public linkQueryParamsHandling: QueryParamsHandling = 'merge';

	/**
	 * Emit onto the linkClicked event when the link is active. This way we can
	 * track the link not when it is clicked, but when it is active. This prevents
	 * multiple problems:
	 * 1. The link is already active when the user clicks it. There is no navigation,
	 * but the event is still emitted.
	 * 1. A programmatic navigation is done: the event is not emitted.
	 *
	 * This approach resolves both problems above.
	 *
	 * @param isActive Whether the link is active or not.
	 */
	public handleLickActive(isActive: boolean): void {
		if (isActive) {
			this.linkClicked.emit();
		}
	}
}
